import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import {
  isDashboardOrgSegment,
  readDashboardOrgCookie,
  writeDashboardOrgCookie,
} from "@/lib/dashboardOrg";

export type OneOrgContextResult = {
  orgId: string | null;
  companyName: string | null;
  source: "session" | "cookie" | "orgs" | "profile" | "none";
  /**
   * Always false on One Pages. We stamp `dashboard-org` only and do not POST
   * `/api/auth/session` (proxied CSRF/`__Host-` cookies can clear the shared
   * `.atw.net` session and look like an auto logout).
   */
  sessionUpdated: boolean;
  error?: string;
};

type MeOrgRow = {
  orgId?: string;
  companyName?: string | null;
  domain?: string | null;
};

type ProfileResponse = {
  activeOrg?: { companyName?: string | null; orgId?: string | null };
  org?: { companyName?: string | null; orgId?: string | null };
  memberships?: Array<{ orgId?: string; companyName?: string | null }>;
};

let cached: OneOrgContextResult | null = null;
let inflight: Promise<OneOrgContextResult> | null = null;

function asOrgId(raw: string | null | undefined): string | null {
  const v = (raw || "").trim();
  return isDashboardOrgSegment(v) ? v : null;
}

async function fetchMeOrgs(): Promise<MeOrgRow[]> {
  const res = await fetch(apiUrl("/api/me/orgs"), {
    cache: "no-store",
    ...apiFetchInit(),
  });
  if (!res.ok) return [];
  const data = (await res.json()) as { orgs?: MeOrgRow[] };
  return Array.isArray(data.orgs) ? data.orgs : [];
}

async function fetchProfileActiveOrg(orgIdHint?: string | null): Promise<{
  orgId: string | null;
  companyName: string | null;
}> {
  const qs = orgIdHint ? `?orgId=${encodeURIComponent(orgIdHint)}` : "";
  const res = await fetch(apiUrl(`/api/me/profile${qs}`), {
    cache: "no-store",
    ...apiFetchInit(),
  });
  if (!res.ok) return { orgId: null, companyName: null };
  const j = (await res.json()) as ProfileResponse;
  const fromActive = j.activeOrg;
  const fromOrg = j.org;
  const orgId =
    asOrgId(fromActive?.orgId) ||
    asOrgId(fromOrg?.orgId) ||
    asOrgId(j.memberships?.find((m) => asOrgId(m.orgId))?.orgId);
  const companyName =
    fromActive?.companyName?.trim() ||
    fromOrg?.companyName?.trim() ||
    null;
  return { orgId, companyName };
}

async function resolveOnce(sessionOrgId: string | null | undefined): Promise<OneOrgContextResult> {
  const fromSession = asOrgId(sessionOrgId);
  const fromCookie = readDashboardOrgCookie();

  let orgId = fromSession || fromCookie;
  let source: OneOrgContextResult["source"] = fromSession
    ? "session"
    : fromCookie
      ? "cookie"
      : "none";
  let companyName: string | null = null;

  if (!orgId) {
    const orgs = await fetchMeOrgs();
    const first = orgs
      .map((o) => ({
        orgId: asOrgId(o.orgId),
        companyName: o.companyName?.trim() || o.domain?.trim() || null,
      }))
      .find((o) => o.orgId);
    if (first?.orgId) {
      orgId = first.orgId;
      companyName = first.companyName;
      source = "orgs";
    }
  }

  if (!orgId) {
    const profile = await fetchProfileActiveOrg(null);
    if (profile.orgId) {
      orgId = profile.orgId;
      companyName = profile.companyName;
      source = "profile";
    }
  }

  if (!orgId) {
    return {
      orgId: null,
      companyName: null,
      source: "none",
      sessionUpdated: false,
      error: "No organization is assigned to this account yet.",
    };
  }

  writeDashboardOrgCookie(orgId);

  if (!companyName) {
    const profile = await fetchProfileActiveOrg(orgId);
    companyName = profile.companyName;
  }

  return { orgId, companyName, source, sessionUpdated: false };
}

/**
 * Resolve the active org (session → dashboard-org cookie → /api/me/orgs → profile),
 * then stamp the cookie so knowledge and other org APIs work without an /app/org/… link.
 */
export function ensureOneOrgContext(
  sessionOrgId?: string | null,
): Promise<OneOrgContextResult> {
  if (cached?.orgId) {
    return Promise.resolve({ ...cached, sessionUpdated: false });
  }
  if (inflight) return inflight;
  inflight = resolveOnce(sessionOrgId)
    .then((result) => {
      cached = result;
      return result;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function peekOneOrgContext(): OneOrgContextResult | null {
  return cached;
}

export function resetOneOrgContextCache(): void {
  cached = null;
  inflight = null;
}
